import React, { useEffect, useRef, useState } from 'react'

import { Disclosure } from 'api/lender/getLenderDisclosures'
import SecondCheckpointModal from 'components/CheckpointModal/SecondCheckpointModal'
import VerifiedIcon from 'components/Icons/VerifiedIcon'
import RoadrunnerDealershipAgreementNotice from 'components/notices/RoadrunnerDealershipAgreementNotice'
import { extendFieldInstructionAny, FormValues } from 'containers/FormBuilder'
import ReturnCustomerModal from 'containers/ReturnCustomer/ReturnCustomerModal'
import {
    ApplicationDataType,
    ActiveLoanType,
} from 'containers/ReturnCustomer/types/returnCustomerTypes'
import { useFeatureFlagContext } from 'contexts/FeatureFlagContextProvider'
import { useLender } from 'contexts/LenderContextProvider'
import { INITIAL_OPE_UNITS } from 'form/fields/opeEquipmentFields'
import useLenderDisclosures from 'hooks/useLenderDisclosures'
import useOpeEquipmentValidator from 'hooks/useOpeEquipmentValidator'
import useUser from 'hooks/useUser'
import LoadingPage from 'pages/LoadingPage/LoadingPage'
import AssetType from 'state/enums/AssetType'
import DisclosureType from 'state/enums/DisclosureType'

import ApplicationFormBuilder, {
    ApplicationFormBuilderProps,
} from '../ApplicationFormBuilder'
import DeveloperFormDataQuickFill from '../DeveloperFormDataQuickFill/DeveloperFormDataQuickFill'
import {
    DISCLOSURE_LOAD_ERROR_MESSAGE,
    hasRequiredDisclosures,
} from '../disclosureUtils'
import { powersportsFormInstructions } from '../PowersportsApplication/powersportsApplicationFields'
import {
    Label,
    MainContainer,
    Title,
    Titlebar,
} from '../PowersportsApplication/PowersportsApplicationFormBuilder.styled'
import {
    COAPPLICANT_TAG,
    isApplicationJointFunc,
    isCoapplicantAddressSameFunc,
    applicantCitizenshipStatusLinkingFunc,
    coapplicantCitizenshipStatusLinkingFunc,
} from '../sharedInstructions'
import submitPowersportsLikeApplication from '../submitPowersportsLikeApplication'
import { swapApplicantsFunc } from '../utils'

type OPEApplicationFormProps = {
    overrideDefaultInitialValues?: ApplicationFormBuilderProps['overrideDefaultInitialValues']
    isLoading?: boolean
    isCopy?: boolean
    parentId?: number
    parentUuid?: string
    isReadOnly?: boolean
    crossoverApplication?: boolean
}

const submitApplication = async (
    uuid: string,
    values: FormValues,
    setShowSubmissionModal: (state: boolean) => void,
    setShowReturnCustomerModal: (state: boolean) => void,
    setApplicationData: (state: ApplicationDataType) => void,
    setActiveLoans: (state: ActiveLoanType[]) => void,
    returnCustomerPsApp2FeatureFlag: boolean | undefined,
    finalize: boolean,
    isCopy?: boolean,
    parentId?: number,
    disclosures?: Disclosure[],
): Promise<string> => {
    return submitPowersportsLikeApplication({
        uuid,
        values,
        setShowSubmissionModal,
        setShowReturnCustomerModal,
        setApplicationData,
        setActiveLoans,
        returnCustomerPsApp2FeatureFlag,
        finalize,
        isCopy,
        parentId,
        disclosures,
        useCollateralsAssetsEndpoint: true,
    })
}

export default function OPEApplicationForm({
    overrideDefaultInitialValues,
    isLoading,
    isCopy,
    parentId,
    parentUuid,
    isReadOnly,
    crossoverApplication,
}: Readonly<OPEApplicationFormProps>): JSX.Element {
    const [uuid, setUuid] = useState('')
    const [showSubmissionModal, setShowSubmissionModal] = useState(false)
    const finalizeRef = useRef(false)
    const [activeLoans, setActiveLoans] = useState<ActiveLoanType[]>([])

    const [
        isAgreementSignedWithRoadrunner,
        setIsAgreementSignedWithRoadrunner,
    ] = useState<boolean>()

    const featureFlags = useFeatureFlagContext()

    const dealershipLenderAgreementValidation =
        featureFlags?.dealershipLenderAgreementValidation

    const useOpeSearchAsYouTypeValidation = Boolean(
        featureFlags?.enableOpeSearchAsYouType,
    )

    const opeEquipmentValidator = useOpeEquipmentValidator(
        useOpeSearchAsYouTypeValidation,
    )

    const akHiEnabled = featureFlags?.akHiStateEnabled ?? false
    const { user } = useUser()
    const { id: lenderId } = useLender()

    const { disclosures, loading, retryAttemptsExhausted } =
        useLenderDisclosures(lenderId)

    const newInstructions = {
        ...powersportsFormInstructions(false, isReadOnly, akHiEnabled),
        financeManagerFields: undefined,
    }

    newInstructions.collateralFields.vehicle = extendFieldInstructionAny(
        newInstructions.collateralFields.vehicle,
        (instruction) => {
            return {
                ...instruction,
                config: {
                    ...instruction.config,
                    assetType: AssetType.OUTDOOR_POWER_EQUIPMENT,
                    ...(useOpeSearchAsYouTypeValidation
                        ? {
                              initialValue: {
                                  ...instruction.config.initialValue,
                                  opeUnits: INITIAL_OPE_UNITS,
                              },
                              validator: opeEquipmentValidator,
                          }
                        : {}),
                },
            }
        },
    )

    useEffect(() => {
        setIsAgreementSignedWithRoadrunner(
            user?.dealership?.isAgreementSignedWithRoadrunner,
        )
    }, [user?.dealership?.isAgreementSignedWithRoadrunner])

    const [applicationData, setApplicationData] = useState<
        ApplicationDataType | undefined
    >()

    const [developerOverrideData, setDeveloperOverrideData] =
        useState<ApplicationDataType>()

    const [showReturnCustomerModal, setShowReturnCustomerModal] =
        useState(false)

    const returnCustomerPsApp2FeatureFlag = featureFlags?.returnCustomerPsApp2

    if (isLoading) {
        return <LoadingPage />
    }

    const initialValuesOverride =
        developerOverrideData || overrideDefaultInitialValues

    const disclosuresLoaded = hasRequiredDisclosures(disclosures, [
        DisclosureType.APPLICATION_FORM_DISCLOSURE,
        DisclosureType.CUSTOMER_VERIFICATION_DISCLOSURE,
    ])

    const disclosureLoadFailed = !loading && retryAttemptsExhausted

    const canSubmit =
        !(
            dealershipLenderAgreementValidation === true &&
            isAgreementSignedWithRoadrunner === false
        ) &&
        !loading &&
        disclosuresLoaded

    return (
        <MainContainer>
            <RoadrunnerDealershipAgreementNotice />
            {disclosureLoadFailed && (
                <p role="alert" data-testid="disclosure-load-error-message">
                    {DISCLOSURE_LOAD_ERROR_MESSAGE}
                </p>
            )}

            <ReturnCustomerModal
                applicationData={applicationData}
                activeLoans={activeLoans}
                isOpen={showReturnCustomerModal}
                handleClose={() => setShowReturnCustomerModal(false)}
            />
            <SecondCheckpointModal isOpen={showSubmissionModal} animated />

            {!isReadOnly && (
                <Titlebar>
                    <Title data-testid="newapplication-title">
                        {isCopy ? 'Copy Into New' : 'New Application'}
                    </Title>
                    <Label data-testid="newapplication-label">
                        Instant decisions • No credit impact &nbsp;
                        <VerifiedIcon />
                    </Label>
                </Titlebar>
            )}

            <DeveloperFormDataQuickFill
                setDeveloperOverrideData={setDeveloperOverrideData}
            />

            <ApplicationFormBuilder
                onSubmit={async (values) => {
                    const newUuid = await submitApplication(
                        uuid,
                        values,
                        setShowSubmissionModal,
                        setShowReturnCustomerModal,
                        setApplicationData,
                        setActiveLoans,
                        returnCustomerPsApp2FeatureFlag,
                        finalizeRef.current,
                        isCopy,
                        parentId,
                        disclosures,
                    )

                    setUuid(newUuid)
                }}
                instructions={newInstructions}
                coapplicantTag={COAPPLICANT_TAG}
                isApplicationJointFunc={isApplicationJointFunc}
                showCitizenshipStatusDisclaimer={
                    applicantCitizenshipStatusLinkingFunc
                }
                showCoapplicantCitizenshipStatusDisclaimer={
                    coapplicantCitizenshipStatusLinkingFunc
                }
                showConsumerShoppedCollateralNotice={Boolean(
                    isCopy && parentUuid,
                )}
                swapApplicantsFunc={swapApplicantsFunc}
                overrideDefaultInitialValues={{
                    ...initialValuesOverride,
                }}
                enableReinitialize
                isCoapplicantAddressSameFunc={isCoapplicantAddressSameFunc}
                setFinalize={(state) => {
                    finalizeRef.current = state
                }}
                canSubmit={canSubmit}
                disclosureLoadFailed={disclosureLoadFailed}
                isReadOnly={isReadOnly}
                parentUuid={parentUuid}
                assetType={AssetType.OUTDOOR_POWER_EQUIPMENT}
                isCrossoverApplication={crossoverApplication}
            />
        </MainContainer>
    )
}

OPEApplicationForm.defaultProps = {
    overrideDefaultInitialValues: undefined,
    isCopy: false,
    isLoading: false,
    parentId: undefined,
    parentUuid: undefined,
    isReadOnly: false,
}

export const testExports = {
    submitApplication,
}
